// =============================================================================
// requireWritten — a write counts only when the server hands back what it wrote.
//
// A plain UPDATE (or DELETE) that row security refuses, or that targets a row
// that does not exist, matches ZERO rows and returns NO error. Postgres drops
// the rows an UPDATE's USING clause refuses without a word (only WITH CHECK
// raises), PostgREST answers 200, and supabase-js resolves
// `{ data: null | [], error: null }`. A mutationFn that only checks `error`
// therefore resolves, react-query calls it a success, and the screen ticks
// "Saved" over a write that never happened. (Every packhouse_config save on
// Config did exactly this before the helper existed.)
//
// So ask the write to RETURN the row — `.select('…')`, or
// `.select('…').maybeSingle()` (safe on a write: maybeSingle turns zero rows
// into `data: null` client-side, never an error) — and hand the whole result
// here:
//
//   requireWritten(
//     await withTimeout(db.from('t').update(patch).eq('tenant_id', id)
//       .select('tenant_id').maybeSingle(), 'label'),
//     'Nothing was saved — …')
//
//   * `error`          → throwFriendly(error, domain): an operator sentence,
//                        never the driver text (a ValidationError, as before).
//   * no row came back → NothingWrittenError(notSaved), carrying YOUR sentence.
//                        Zero rows cannot say WHY — a refused role and a
//                        missing row look identical from here — so the
//                        sentence has to cover both: who can make the change,
//                        and to report it if that person sees it too.
//   * otherwise        → the row written (the first, for an array result).
//
// NothingWrittenError is a plain Error on purpose, NOT a ValidationError, so
// it reaches the error reporter: zero rows is also what a missing row looks
// like, and that one is ours to fix. Same call as updateWithVersion's
// WriteBlockedError.
//
// A table with a `version` column wants updateWithVersion (src/lib/optimistic.ts)
// instead — it probes the row to tell a stale write from a refused one.
// =============================================================================

import { throwFriendly } from '@/lib/friendlyError'

/** A write the server accepted without error that changed no row. */
export class NothingWrittenError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'NothingWrittenError'
  }
}

/**
 * The row a write returned, or a throw. `res` is the whole PostgREST result
 * of a write that `.select()`ed what it wrote; `notSaved` is the operator
 * sentence for "no row came back"; `domain` goes to throwFriendly for a
 * screen-specific refusal sentence.
 */
export function requireWritten<T>(
  res: { data: T | readonly T[] | null; error: unknown },
  notSaved: string,
  domain?: [RegExp, string][],
): T {
  if (res.error) throwFriendly(res.error, domain)
  const row = Array.isArray(res.data) ? (res.data as readonly T[])[0] : (res.data as T | null)
  if (row == null) throw new NothingWrittenError(notSaved)
  return row
}
